import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Avatar
} from '@mui/material';
import {
  AttachMoney,
  CalendarMonth,
  Search,
  FilterList,
  PictureAsPdf,
  TableChart,
  AccountBalance,
  CreditCard,
  Visibility,
  Assessment,
  Clear
} from '@mui/icons-material';
import HttpsIcon from '@mui/icons-material/Https';
import { Input, message, ConfigProvider, DatePicker } from 'antd';
import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  ComposedChart
} from 'recharts';
import { exportMonthlyPaymentsToPdf } from '../utils/PdfExport';
import { exportMonthlyPaymentsToExcel } from '../utils/ExcelExport';
import LoadingInformation from '../../Components/LoadingInformation';

// Configurar moment en español
moment.locale('es');

const { RangePicker } = DatePicker;

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const COLORS = ['#f759ab', '#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4'];

const formatDate = () => {
  const fecha = new Date();
  return new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(fecha);
};


function MonthlyPaymentReport() {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('todos');
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [permissionError, setPermissionError] = useState(false);

  // Summary metrics
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalAmount: 0,
    averagePayment: 0,
    transferAmount: 0,
    cashAmount: 0,
    paymentsByMethod: {},
    weeklyTotals: [],
    dailyAverage: 0,
    bestDay: '',
    bestDayAmount: 0,
    largestPayment: 0,
    smallestPayment: 0
  });

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const currentDate = moment();
  const selectedMoment = moment().year(selectedYear).month(selectedMonth - 1);

  // Generar años disponibles (últimos 3 años + año actual)
  const availableYears = Array.from({ length: 4 }, (_, i) => currentDate.year() - i);

  // Configurar las fechas permitidas (solo del mes y año seleccionado)
  const disabledDate = (current) => {
    if (!current) return false;
    return !current.isSame(selectedMoment, 'month');
  };

  const getPayments = async () => {
    setLoading(true);
    setPermissionError(false);
    try {
      // Construir la URL con mes y año seleccionados
      const response = await fetch(`https://dprincess-training.servehttp.com/payments/month/${selectedYear}/${selectedMonth}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        
        // Normalizar los datos para la tabla y cálculos
        const allPayments = data.map(payment => ({
          paymentID: payment.transactionID || payment.cashFlowID,
          invoiceID: payment.invoiceID,
          invoiceNumber: payment.invoiceNumber || `${payment.invoiceID}`,
          clientName: payment.clientName || 'Cliente no especificado',
          amount: parseNumber(payment.totalAmountReceived || payment.amount),
          paymentDate: payment.transactionDate || payment.creationDate,
          paymentMethod: payment.paymentMethod || 'efectivo',
          description: payment.description || payment.notes || '',
          employeeName: payment.employeeName || 'N/A'
        }));
        
        setPayments(allPayments);
        setFilteredPayments(allPayments);
        calculateSummary(allPayments);
      } else {
        messageApi.error('Error al cargar los pagos del mes seleccionado');
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (paymentData) => {
    const totalPayments = paymentData.length;
    const totalAmount = paymentData.reduce((sum, payment) => sum + payment.amount, 0);
    const averagePayment = totalPayments > 0 ? totalAmount / totalPayments : 0;

    // Calcular métricas por método de pago
    const transferAmount = paymentData
      .filter(payment => (payment.paymentMethod || 'Efectivo').toLowerCase() === 'transferencia')
      .reduce((sum, payment) => sum + payment.amount, 0);

    const cashAmount = paymentData
      .filter(payment => (payment.paymentMethod || 'Efectivo').toLowerCase() !== 'transferencia')
      .reduce((sum, payment) => sum + payment.amount, 0);

    // Pago más alto y más bajo
    const amounts = paymentData.map(p => p.amount).filter(a => a > 0);
    const largestPayment = amounts.length > 0 ? Math.max(...amounts) : 0;
    const smallestPayment = amounts.length > 0 ? Math.min(...amounts) : 0;

    // Promedio diario (basado en días del mes seleccionado)
    const selectedMonthMoment = moment().year(selectedYear).month(selectedMonth - 1);
    const daysInMonth = selectedMonthMoment.daysInMonth();
    const isCurrentMonth = selectedMonthMoment.isSame(moment(), 'month');
    const daysPassed = isCurrentMonth ? moment().date() : daysInMonth;
    const dailyAverage = daysPassed > 0 ? totalAmount / daysPassed : 0;

    // Mejor día del mes
    const dailyTotals = {};
    paymentData.forEach(payment => {
      const day = moment(payment.paymentDate).format('YYYY-MM-DD');
      if (!dailyTotals[day]) {
        dailyTotals[day] = 0;
      }
      dailyTotals[day] += payment.amount;
    });

    const bestDayEntry = Object.entries(dailyTotals).reduce((max, [day, amount]) =>
      amount > max[1] ? [day, amount] : max, ['', 0]
    );

    // Totales por semana del mes seleccionado
    const weeklyTotals = [];
    const monthStart = selectedMonthMoment.clone().startOf('month');
    for (let week = 0; week < 5; week++) {
      const weekStart = monthStart.clone().add(week, 'week').startOf('week');
      const weekEnd = weekStart.clone().endOf('week');
      const weekPayments = paymentData.filter(payment => {
        const paymentDate = moment(payment.paymentDate);
        return paymentDate.isSameOrAfter(weekStart, 'day') &&
          paymentDate.isSameOrBefore(weekEnd, 'day') &&
          paymentDate.isSame(monthStart, 'month');
      });
      const weekAmount = weekPayments.reduce((sum, payment) => sum + payment.amount, 0);
      if (weekAmount > 0 || week < 4) {
        weeklyTotals.push({
          week: `Semana ${week + 1}`,
          amount: weekAmount,
          count: weekPayments.length,
          period: `${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM')}`
        });
      }
    }

    // Agrupar por método de pago
    const paymentsByMethod = {};
    paymentData.forEach(payment => {
      const method = payment.paymentMethod || 'Efectivo';
      if (!paymentsByMethod[method]) {
        paymentsByMethod[method] = { count: 0, amount: 0 };
      }
      paymentsByMethod[method].count++;
      paymentsByMethod[method].amount += payment.amount;
    });

    setSummary({
      totalPayments,
      totalAmount,
      averagePayment,
      transferAmount,
      cashAmount,
      paymentsByMethod,
      weeklyTotals,
      dailyAverage,
      bestDay: bestDayEntry[0] ? moment(bestDayEntry[0]).format('DD/MM/YYYY') : '',
      bestDayAmount: bestDayEntry[1],
      largestPayment,
      smallestPayment
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...payments];

    // Filtrar por rango de fechas
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter(payment => {
        const paymentDate = moment(payment.paymentDate);
        return paymentDate.isSameOrAfter(startDate, 'day') &&
          paymentDate.isSameOrBefore(endDate, 'day');
      });
    }

    // Filtrar por método de pago
    if (paymentMethod !== 'todos') {
      filtered = filtered.filter(payment =>
        (payment.paymentMethod || 'Efectivo').toLowerCase() === paymentMethod.toLowerCase()
      );
    }

    // Filtrar por término de búsqueda
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.invoiceNumber?.toString().toLowerCase().includes(searchTerm) ||
        payment.clientName?.toLowerCase().includes(searchTerm) ||
        payment.description?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredPayments(filtered);
    calculateSummary(filtered);
  }, [payments, search, paymentMethod, dateRange]);

  const clearDateFilter = () => {
    setDateRange(null);
  };

  const prepareChartData = () => {
    // Datos diarios del mes seleccionado
    const dailyData = {};
    const monthStart = selectedMoment.clone().startOf('month');
    const monthEnd = selectedMoment.clone().endOf('month');

    // Inicializar todos los días del mes
    for (let date = monthStart.clone(); date.isSameOrBefore(monthEnd, 'day'); date.add(1, 'day')) {
      const day = date.format('DD/MM');
      dailyData[day] = { day, amount: 0, count: 0 };
    }

    // Llenar con datos reales
    filteredPayments.forEach(payment => {
      const day = moment(payment.paymentDate).format('DD/MM');
      if (dailyData[day]) {
        dailyData[day].amount += payment.amount;
        dailyData[day].count += 1;
      }
    });

    // Datos por método de pago
    const methodData = Object.entries(summary.paymentsByMethod).map(([method, data]) => ({
      method,
      amount: data.amount,
      count: data.count,
      color: COLORS[Object.keys(summary.paymentsByMethod).indexOf(method) % COLORS.length]
    }));

    return {
      daily: Object.values(dailyData),
      methods: methodData,
      weekly: summary.weeklyTotals
    };
  };

  const handleExportPDF = () => {
    exportMonthlyPaymentsToPdf(filteredPayments);
  };

  const handleExportExcel = () => {
    exportMonthlyPaymentsToExcel(filteredPayments);
  };

  useEffect(() => {
    getPayments();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (payments.length > 0) {
      applyFilters();
    }
  }, [search, paymentMethod, dateRange, payments, applyFilters]);

  const chartData = prepareChartData();

  if (loading) return <LoadingInformation />;

  // Mostrar mensaje de falta de permisos si corresponde
  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <ConfigProvider locale={esES}>
      <div style={{ background: '#f3e8ff', minHeight: '100vh', padding: '20px 0' }}>
        <Container maxWidth="xl">
          {contextHolder}

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                color: '#4b0082',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: {
                  xs: '1.5rem',    // h5 size en móviles
                  sm: '2.125rem',  // h2 size en tablets
                  md: '3rem'       // h2 size en desktop
                }
              }}
            >
              <CalendarMonth sx={{
                fontSize: { xs: 30, sm: 50 }, // Icono más pequeño en móviles
                color: '#f759ab'
              }} />
              Reporte de Pagos de {selectedMoment.format('MMMM YYYY')}
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }} // Columna en móviles, fila en desktop
              spacing={2}
              sx={{ mb: 3 }}
            >
              {/* Selector de Año */}
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: '#4b0082' }}>Año</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Año"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                  }}
                >
                  {availableYears.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Selector de Mes */}
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: '#4b0082' }}>Mes</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Mes"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                  }}
                >
                  {[
                    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                  ].map((month, index) => (
                    <MenuItem key={index + 1} value={index + 1}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                variant="contained"
                sx={{
                  backgroundColor: '#f759ab',
                  '&:hover': { backgroundColor: '#e04897' }
                }}
              >
                Exportar PDF
              </Button>
              <Button
                startIcon={<TableChart />}
                onClick={handleExportExcel}
                variant="contained"
                sx={{
                  backgroundColor: '#4b0082',
                  '&:hover': { backgroundColor: '#3730a3' },
                  display: { xs: 'none', sm: 'flex' } // Ocultar en móviles
                }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Box>

          {/* Filters - Agregado el filtro de fechas */}
          <Card sx={{ mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#4b0082',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FilterList sx={{ mr: 1, color: '#f759ab' }} />
                Filtros de Búsqueda
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                    <Input
                      placeholder="Buscar por factura, cliente o descripción"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                      size="large"
                      prefix={<Search />}
                    />
                  </ConfigProvider>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#4b0082' }}>Método de pago</InputLabel>
                    <Select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      label="Método de pago"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="efectivo">Efectivo</MenuItem>
                      <MenuItem value="tarjeta">Tarjeta</MenuItem>
                      <MenuItem value="transferencia">Transferencia</MenuItem>
                      <MenuItem value="cheque">Cheque</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                      <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        disabledDate={disabledDate}
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        format="DD/MM/YYYY"
                        size="large"
                        style={{ width: '100%' }}
                        locale={esES}
                      />
                    </ConfigProvider>
                    {dateRange && (
                      <Tooltip title="Limpiar filtro de fecha">
                        <IconButton
                          onClick={clearDateFilter}
                          size="small"
                          sx={{
                            color: '#f759ab',
                            '&:hover': { backgroundColor: 'rgba(247, 89, 171, 0.1)' }
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Indicador del filtro activo */}
              {(dateRange || paymentMethod !== 'todos' || search.trim()) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dateRange && (
                    <Chip
                      label={`${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`}
                      onDelete={clearDateFilter}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {paymentMethod !== 'todos' && (
                    <Chip
                      label={`Método: ${paymentMethod}`}
                      onDelete={() => setPaymentMethod('todos')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {search.trim() && (
                    <Chip
                      label={`Búsqueda: ${search}`}
                      onDelete={() => setSearch('')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #22c55e', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d' }}>
                        {formatCurrency(summary.totalAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total {dateRange ? 'filtrado' : 'del mes'}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: '#22c55e' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #8b5cf6', background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#6b21a8' }}>
                        {summary.totalPayments}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cantidad de pagos
                      </Typography>
                    </Box>
                    <Assessment sx={{ fontSize: 40, color: '#8b5cf6' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #3b82f6', background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                        {formatCurrency(summary.transferAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Por Transferencia
                      </Typography>
                    </Box>
                    <AccountBalance sx={{ fontSize: 40, color: '#3b82f6' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #f97316', background: 'linear-gradient(135deg, #fed7aa, #fdba74)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2410c' }}>
                        {formatCurrency(summary.cashAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Efectivo
                      </Typography>
                    </Box>
                    <CreditCard sx={{ fontSize: 40, color: '#f97316' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 4, alignItems: 'center' }}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Ingresos Diarios del Mes - {selectedMoment.format('MMMM YYYY')}
                  {dateRange && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Filtrado: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
                    </Typography>
                  )}
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={chartData.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === 'amount' ? formatCurrency(value) : value,
                        name === 'amount' ? 'Monto' : 'Cantidad'
                      ]}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#f759ab"
                      fill="#f759ab"
                      fillOpacity={0.3}
                      name="Monto"
                    />
                    <Bar dataKey="count" fill="#22c55e" name="Cantidad de Pagos" />
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Estadísticas adicionales */}
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082', mb: 1 }}>
                    Estadísticas {dateRange ? 'del Período Filtrado' : 'del Mes'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Total de pagos:</Typography>
                    <Typography variant="caption" fontWeight={600}>{summary.totalPayments}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Promedio por pago:</Typography>
                    <Typography variant="caption" fontWeight={600}>{formatCurrency(summary.averagePayment)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Pago más alto:</Typography>
                    <Typography variant="caption" fontWeight={600}>{formatCurrency(summary.largestPayment)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">Pago más bajo:</Typography>
                    <Typography variant="caption" fontWeight={600}>{formatCurrency(summary.smallestPayment)}</Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Métodos de Pago
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.methods}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ method, percent }) => `${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {chartData.methods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Leyenda personalizada */}
                <Box sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  borderTop: '1px solid #e0e7ff',
                  pt: 2
                }}>
                  {chartData.methods.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: entry.color,
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {entry.method}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: '#4b0082',
                        fontSize: '0.85rem'
                      }}>
                        {formatCurrency(entry.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Grid>

            {/* Weekly Breakdown Chart */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Distribución Semanal
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData.weekly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === 'amount' ? formatCurrency(value) : value,
                        name === 'amount' ? 'Monto' : 'Cantidad'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="amount" fill="#f759ab" name="Monto Semanal" />
                    <Bar dataKey="count" fill="#22c55e" name="Cantidad de Pagos" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Grid>
          </Grid>

          {/* Payments Table */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                Detalle de Pagos - {selectedMoment.format('MMMM YYYY')} ({filteredPayments.length} registros)
                {dateRange && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Mostrando pagos del {dateRange[0].format('DD/MM/YYYY')} al {dateRange[1].format('DD/MM/YYYY')}
                  </Typography>
                )}
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Factura</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Método</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Monto</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Descripción</TableCell> {/* Nueva columna */}
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Atendido por</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((payment, index) => (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                          <TableCell>
                            {payment.paymentDate
                              ? moment(payment.paymentDate).format('DD/MM/YYYY hh:mm A')
                              : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                            {payment.invoiceNumber}
                          </TableCell>
                          <TableCell>{payment.clientName}</TableCell>
                          <TableCell>
                            <Chip
                              icon={payment.paymentMethod === 'efectivo' ? <AttachMoney /> :
                                payment.paymentMethod === 'Tarjeta' ? <CreditCard /> :
                                  <AccountBalance />}
                              label={payment.paymentMethod.toUpperCase() || 'Efectivo'}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                  payment.paymentMethod === 'tarjeta' ? '#3b82f6' : '#f97316',
                                borderColor: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                  payment.paymentMethod === 'tarjeta' ? '#3b82f6' : '#f97316'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#22c55e' }}>
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell>
                            {payment.description || '—'}
                          </TableCell>
                          <TableCell>
                            {payment.employeeName || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver factura">
                              <IconButton
                                onClick={() => navigate(`/billing/invoice/${payment.invoiceID}`)}
                                sx={{ color: '#f759ab' }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredPayments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </CardContent>
          </Card>
        </Container>
      </div>
    </ConfigProvider>
  );
}

export default MonthlyPaymentReport;